/**
 * IPPON Trading 共通カラーパレット
 *
 * このファイルは、IPPON Trading の全アプリケーションで使用される
 * 統一されたカラーパレットを定義します。
 *
 * 使用方法:
 * import { colors } from '@ippon/shared-components';
 *
 * // CSS変数として使用
 * color: colors.primary.main;
 *
 * // Material-UIのsx propで使用
 * sx={{ color: colors.text.primary }}
 */

export const colors = {
  // プライマリカラー（ブランドカラー）
  primary: {
    main: "#0B9DBD", // メインのブランドカラー
    light: "#BFE6EF", // ライトバージョン
    dark: "#0A8BA8", // ダークバージョン
    hover: "#0A8BA8", // ホバー時の色
    active: "#0A8BA8", // アクティブ時の色
  },

  // セカンダリカラー
  secondary: {
    main: "#1976D2", // Material-UIのデフォルトプライマリ
    light: "#E3F2FD", // ライトバージョン
    dark: "#1565C0", // ダークバージョン
  },

  // テキストカラー
  text: {
    primary: "#3E3E3E", // メインテキスト
    secondary: "#989898", // セカンダリテキスト
    disabled: "#989898", // 無効化されたテキスト
    placeholder: "#989898", // プレースホルダー
    inverse: "#FFFFFF", // 反転テキスト（白背景など）
  },

  // エラー・警告・成功カラー
  status: {
    error: "#DA384C", // エラー
    errorBackground: "#FFEBEE", // エラーボタンの背景色
    warning: "#FF9800", // 警告
    success: "#4CAF50", // 成功
    info: "#2196F3", // 情報
    confirm: "#E91E63", // 確認ボタンのテキスト/ボーダー色
    confirmBackground: "#FFEBEE", // 確認ボタンの背景色
  },

  // ボーダーカラー
  border: {
    light: "#E0E0E0", // ライトボーダー
    medium: "#989898", // ミディアムボーダー
    dark: "#BDBDBD", // ダークボーダー
    tab: "#d9d9d9", // タブナビゲーションのボーダー
    focus: "#0B9DBD", // フォーカス時のボーダー
    error: "#DA384C", // エラー時のボーダー
  },

  // 背景カラー
  background: {
    default: "#FFFFFF", // デフォルト背景
    paper: "#FFFFFF", // ペーパー背景
    light: "#F5F5F5", // ライト背景
    lighter: "#F8F8F8", // よりライトな背景
    disabled: "#F5F5F5", // 無効化された背景
    hover: "#F5F5F5", // ホバー時の背景
    selected: "rgba(11, 157, 189, 0.08)", // 選択時の背景
    overlay: "rgba(0, 0, 0, 0.1)", // オーバーレイ背景
  },

  // チャット送信停止ボタン
  chatCancelButton: {
    background: "#000000",
    icon: "#FFFFFF",
    border: "#E0E0E0",
    hover: {
      background: "#000000",
      opacity: 0.9,
    },
    tooltip: {
      background: "#333333",
      text: "#FFFFFF",
    },
  },

  // シャドウカラー
  shadow: {
    light: "rgba(0, 0, 0, 0.1)", // ライトシャドウ
    medium: "rgba(0, 0, 0, 0.2)", // ミディアムシャドウ
    dark: "rgba(0, 0, 0, 0.3)", // ダークシャドウ
    modal: "0px 1px 24px 0px #5b5b5b40", // モーダルシャドウ
  },

  // アイコンカラー
  icon: {
    primary: "#3E3E3E", // プライマリアイコン
    secondary: "#989898", // セカンダリアイコン
    disabled: "#989898", // 無効化されたアイコン
    active: "#0B9DBD", // アクティブなアイコン
  },

  // AI査定結果チップカラー
  aiAssessment: {
    fair: {
      color: "#00A75F",
      backgroundColor: "rgba(191, 239, 203, 0.4)",
      borderColor: "#bfefcb",
    },
    low: {
      color: "#0B9DBD",
      backgroundColor: "rgba(223, 245, 250, 0.4)",
      borderColor: "#dff5fa",
    },
    high: {
      color: "#FF6A00",
      backgroundColor: "rgba(255, 226, 204, 0.4)",
      borderColor: "#ffe2cc",
    },
    unassessed: {
      color: "#757575",
      backgroundColor: "rgba(224, 224, 224, 0.4)",
      borderColor: "#f5f5f5",
    },
  },

  // チャート・グラフカラー（将来の拡張用）
  chart: {
    primary: "#0B9DBD",
    secondary: "#1976D2",
    tertiary: "#4CAF50",
    quaternary: "#FF9800",
    quinary: "#9C27B0",
  },
} as const;

// CSS変数として使用するための文字列形式
export const cssColors = {
  primary: {
    main: "var(--color-primary-main)",
    light: "var(--color-primary-light)",
    dark: "var(--color-primary-dark)",
    hover: "var(--color-primary-hover)",
    active: "var(--color-primary-active)",
  },
  text: {
    primary: "var(--color-text-primary)",
    secondary: "var(--color-text-secondary)",
    disabled: "var(--color-text-disabled)",
    placeholder: "var(--color-text-placeholder)",
    inverse: "var(--color-text-inverse)",
  },
  status: {
    error: "var(--color-status-error)",
    errorBackground: "var(--color-status-error-background)",
    warning: "var(--color-status-warning)",
    success: "var(--color-status-success)",
    info: "var(--color-status-info)",
    confirm: "var(--color-status-confirm)",
    confirmBackground: "var(--color-status-confirm-background)",
  },
  border: {
    light: "var(--color-border-light)",
    medium: "var(--color-border-medium)",
    dark: "var(--color-border-dark)",
    tab: "var(--color-border-tab)",
    focus: "var(--color-border-focus)",
    error: "var(--color-border-error)",
  },
  background: {
    default: "var(--color-background-default)",
    paper: "var(--color-background-paper)",
    light: "var(--color-background-light)",
    lighter: "var(--color-background-lighter)",
    disabled: "var(--color-background-disabled)",
    hover: "var(--color-background-hover)",
    selected: "var(--color-background-selected)",
    overlay: "var(--color-background-overlay)",
  },
} as const;

// CSS変数を定義するためのCSS文字列
export const cssVariables = `
:root {
  /* Primary Colors */
  --color-primary-main: ${colors.primary.main};
  --color-primary-light: ${colors.primary.light};
  --color-primary-dark: ${colors.primary.dark};
  --color-primary-hover: ${colors.primary.hover};
  --color-primary-active: ${colors.primary.active};

  /* Secondary Colors */
  --color-secondary-main: ${colors.secondary.main};
  --color-secondary-light: ${colors.secondary.light};
  --color-secondary-dark: ${colors.secondary.dark};

  /* Text Colors */
  --color-text-primary: ${colors.text.primary};
  --color-text-secondary: ${colors.text.secondary};
  --color-text-disabled: ${colors.text.disabled};
  --color-text-placeholder: ${colors.text.placeholder};
  --color-text-inverse: ${colors.text.inverse};

  /* Status Colors */
  --color-status-error: ${colors.status.error};
  --color-status-error-background: ${colors.status.errorBackground};
  --color-status-warning: ${colors.status.warning};
  --color-status-success: ${colors.status.success};
  --color-status-info: ${colors.status.info};
  --color-status-confirm: ${colors.status.confirm};
  --color-status-confirm-background: ${colors.status.confirmBackground};

  /* Border Colors */
  --color-border-light: ${colors.border.light};
  --color-border-medium: ${colors.border.medium};
  --color-border-dark: ${colors.border.dark};
  --color-border-tab: ${colors.border.tab};
  --color-border-focus: ${colors.border.focus};
  --color-border-error: ${colors.border.error};

  /* Background Colors */
  --color-background-default: ${colors.background.default};
  --color-background-paper: ${colors.background.paper};
  --color-background-light: ${colors.background.light};
  --color-background-lighter: ${colors.background.lighter};
  --color-background-disabled: ${colors.background.disabled};
  --color-background-hover: ${colors.background.hover};
  --color-background-selected: ${colors.background.selected};
  --color-background-overlay: ${colors.background.overlay};

  /* Icon Colors */
  --color-icon-primary: ${colors.icon.primary};
  --color-icon-secondary: ${colors.icon.secondary};
  --color-icon-disabled: ${colors.icon.disabled};
  --color-icon-active: ${colors.icon.active};
}
`;

// Material-UIテーマ用のカラーパレット
export const muiThemeColors = {
  palette: {
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
    },
    error: {
      main: colors.status.error,
    },
    warning: {
      main: colors.status.warning,
    },
    success: {
      main: colors.status.success,
    },
    info: {
      main: colors.status.info,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    divider: colors.border.light,
  },
};

// 型定義
export type ColorPalette = typeof colors;
export type PrimaryColors = typeof colors.primary;
export type TextColors = typeof colors.text;
export type StatusColors = typeof colors.status;
export type BorderColors = typeof colors.border;
export type BackgroundColors = typeof colors.background;
export type IconColors = typeof colors.icon;

